/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */
import React, { useEffect, useCallback, useState, useRef } from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import { Localization, Typography, Layout, Drawer } from 'connex-cds';
import { AttachmentOutlined } from '@mui/icons-material';
import { useGetDriverAttachment } from '../../../../../../query-hooks/driver-attachment';
import { useTicketContext } from '../../TicketContext';
import style, { ImageAttachment } from './style';
import Viewer from './Viewer';
import { Logger } from '../../../../../../util/log/logger';

const { Container } = Layout;
const { Translate } = Localization;
const { Subtitle } = Typography;

const Styled = styled.div`
  ${style}
`;

const CACHE_NAME_PREFIX = 'mobile-ticket-attachments';

const Attachments = () => {
  const { ticket } = useTicketContext();
  const { openDrawer } = Drawer.useDrawerContext();
  const { data: attachments } = useGetDriverAttachment(ticket?.crn) ?? [];
  const [isLoading, setIsLoading] = useState(false);
  const [processedUrls, setProcessedUrls] = useState(new Map());
  const loadingAttachmentsRef = useRef(new Set());
  const objectUrlsRef = useRef(new Set());

  const getCacheName = useCallback(() => `${CACHE_NAME_PREFIX}-${ticket?.crn}`, [ticket?.crn]);

  const getCacheKey = useCallback(attachment => attachment?.crn, []);

  const getCachedFile = useCallback(
    async cacheKey => {
      try {
        if (!('caches' in window)) {
          return null;
        }

        const cacheName = getCacheName();
        const cache = await caches.open(cacheName);
        const cachedResponse = await cache.match(cacheKey);

        if (cachedResponse) {
          const blob = await cachedResponse.blob();
          return blob;
        }

        return null;
      } catch (error) {
        console.error('Error getting cached file:', error);
        return null;
      }
    },
    [getCacheName]
  );

  const cacheFile = useCallback(
    async (cacheKey, blob) => {
      try {
        if (!('caches' in window)) {
          return;
        }

        const cacheName = getCacheName();
        const cache = await caches.open(cacheName);
        const response = new Response(blob);
        await cache.put(cacheKey, response);
      } catch (error) {
        Logger.error('Error caching file:', error);
      }
    },
    [getCacheName]
  );

  const cleanOldCaches = useCallback(async currentCrn => {
    try {
      if (!('caches' in window)) {
        return;
      }

      const cacheNames = await caches.keys();

      const currentCacheName = `${CACHE_NAME_PREFIX}-${currentCrn}`;

      const deletePromises = cacheNames
        .filter(name => name.startsWith(CACHE_NAME_PREFIX) && name !== currentCacheName)

        .map(name => caches.delete(name));

      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error cleaning old caches:', error);
    }
  }, []);

  const downloadFile = useCallback(
    async attachment => {
      const cacheKey = getCacheKey(attachment);

      const cachedBlob = await getCachedFile(cacheKey);

      if (cachedBlob) {
        const file = new File([cachedBlob], attachment?.fileName, {
          type: attachment?.contentType,
        });

        return {
          file,
          contentType: file.type,
        };
      }

      const response = await fetch(attachment.link, { method: 'GET' }).catch(error => {
        console.error('Error fetching attachment:', error);
        return null;
      });

      if (!response || !response.ok) {
        return null;
      }

      const blob = await response.blob();
      await cacheFile(cacheKey, blob);

      const file = new File([blob], attachment?.fileName, {
        type: attachment?.contentType,
      });

      return {
        file,
        contentType: file.type,
      };
    },
    [getCacheKey, getCachedFile, cacheFile]
  );

  const download = useCallback(
    async e => {
      let attachment = { ...e };

      if (typeof e.link === 'string') {
        const data = await downloadFile(e);
        attachment = { ...attachment, ...data };
      }

      if (!attachment?.file) {
        return null;
      }

      const url = window.URL.createObjectURL(attachment.file);
      objectUrlsRef.current.add(url);
      return url;
    },
    [downloadFile]
  );

  const handleOnclick = useCallback(
    async attachment => {
      const attachmentCrn = attachment?.crn;
      let attachmentLink = attachment?.link;

      if (typeof attachmentLink === 'string' && attachmentLink.startsWith('http')) {
        if (loadingAttachmentsRef.current.has(attachmentCrn)) {
          return;
        }

        loadingAttachmentsRef.current.add(attachmentCrn);
        setIsLoading(true);

        try {
          const url = await download(attachment);

          if (url) {
            attachmentLink = url;
            setProcessedUrls(prev => new Map(prev).set(attachmentCrn, url));
          }
        } finally {
          loadingAttachmentsRef.current.delete(attachmentCrn);
          setIsLoading(false);
        }
      } else {
        const processedUrl = processedUrls.get(attachmentCrn);
        if (processedUrl) {
          attachmentLink = processedUrl;
        }
      }

      if (!attachmentLink) {
        return;
      }

      openDrawer({
        titleStringId: 'attachments',
        width: '100%',
        component: (() => {
          if (attachment?.contentType === 'application/pdf') {
            return <Viewer src={attachmentLink} />;
          }
          if (String(attachment?.contentType).includes('image')) {
            return <ImageAttachment src={attachmentLink} />;
          }
          return <iframe src={attachmentLink} width="100%" height="100%" />;
        })(),
      });
    },
    [openDrawer, download, processedUrls]
  );

  useEffect(() => {
    if (attachments?.[0]) {
      const firstAttachment = attachments[0];
      const attachmentCrn = firstAttachment?.crn;

      if (attachmentCrn && !processedUrls.has(attachmentCrn) && !loadingAttachmentsRef.current.has(attachmentCrn)) {
        loadingAttachmentsRef.current.add(attachmentCrn);
        setIsLoading(true);

        download(firstAttachment)
          .then(url => {
            if (url) {
              setProcessedUrls(prev => new Map(prev).set(attachmentCrn, url));
            }
          })
          .finally(() => {
            loadingAttachmentsRef.current.delete(attachmentCrn);
            setIsLoading(false);
          });
      }
    }
  }, [attachments, download, processedUrls]);

  useEffect(() => {
    if (ticket?.crn) {
      cleanOldCaches(ticket?.crn);
    }
  }, [ticket?.crn, cleanOldCaches]);

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach(url => {
        window.URL.revokeObjectURL(url);
      });
      objectUrlsRef.current.clear();
      setProcessedUrls(new Map());
    };
  }, [ticket?.crn]);

  return (
    <Container flex={1} style={{ padding: '5px 10px' }}>
      <Styled className={cn('attachments')}>
        <Subtitle>
          <Translate stringId="attachments" data-testid="attachmentsLabel" />
        </Subtitle>
        <div className={cn('attachment', { 'is-loading': isLoading })}>
          {attachments?.length > 0 &&
            attachments?.map(attachment => {
              const isAttachmentLoading = loadingAttachmentsRef.current.has(attachment?.crn);

              return (
                <Container
                  key={attachment?.crn}
                  onClick={() => {
                    if (!isAttachmentLoading && !isLoading) {
                      handleOnclick(attachment);
                    }
                  }}
                >
                  {<AttachmentOutlined />}
                  {attachment?.fileName}
                </Container>
              );
            })}
        </div>
      </Styled>
    </Container>
  );
};

export default Attachments;
